import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Rocket,
  Search,
  Filter,
  Pencil,
  Trash2,
  RefreshCw,
  CalendarDays,
  Linkedin,
  Building2,
  X,
  Loader2,
  Save,
  AlertTriangle,
  Sparkles,
  Network,
  Users,
  Send,
  UserPlus,
} from 'lucide-react';
import { useFormController } from '../../Shared/Component/readFormData/useFormController';
import { Alert, AlertProps } from '../../Shared/Utils/Alert';
import Pagination from '../../Shared/Pagination/Pagination';
import {
  initialUserEarlyAccessResponse,
  UserEarlyAccessResponse,
} from '../../LandingScreens/Types/EarlyAccessType';
import {
  deleteEarlyAccess,
  sendLaunchEmailToEarlyAccess,
  updateEarlyAccess,
} from '../../LandingScreens/Service/EarlyAccessService';
import { convertEarlyAccessToProspect } from '../Services/GrowthService';

interface Props {
  data: UserEarlyAccessResponse[];
  menuName: string;
  role?: string;
  onChanged?: () => void;
}

const PAGE_SIZE = 8;

const STATUS_OPTIONS = [
  'EARLY_ACCESS_PENDING',
  'EARLY_ACCESS_EMAIL_SENT',
  'USER_REGISTERED_SKIPPED',
  'EARLY_ACCESS_APPROVED',
  'APPROVED',
  'REJECTED',
  'ACTIVE',
  'INACTIVE',
  'PROSPECT_SENT',
];

/** Once a waitlist row has been converted into a Growth prospect it cannot be converted again. */
const PROSPECT_SENT = 'PROSPECT_SENT';

const labelClass = 'mb-1 block text-[10px] font-black uppercase tracking-widest text-slate-400';
const fieldClass =
  'w-full rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2.5 text-sm font-semibold text-slate-100 outline-none transition placeholder:text-slate-600 focus:border-cyan-500/50 [color-scheme:dark]';

const normalize = (status?: string) => (status || '').toUpperCase();

const statusBadge = (status?: string) => {
  const s = normalize(status);
  if (s === 'USER_REGISTERED_SKIPPED')
    return 'border-violet-500/30 bg-violet-500/10 text-violet-400';
  if (s === 'EARLY_ACCESS_EMAIL_SENT')
    return 'border-sky-500/30 bg-sky-500/10 text-sky-400';
  if (s.includes('APPROVED') || s === 'ACTIVE')
    return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400';
  if (s === PROSPECT_SENT)
    return 'border-indigo-500/30 bg-indigo-500/10 text-indigo-400';
  if (s === 'REJECTED') return 'border-rose-500/30 bg-rose-500/10 text-rose-400';
  if (s.includes('PENDING')) return 'border-amber-500/30 bg-amber-500/10 text-amber-400';
  return 'border-slate-600/40 bg-slate-700/20 text-slate-300';
};

const EarlyAccessUsersComponent = ({ data, menuName, role, onChanged }: Props) => {
  const [selected, setSelected] = useState<UserEarlyAccessResponse | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isSendAllOpen, setIsSendAllOpen] = useState(false);
  const [isConvertOpen, setIsConvertOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sendingAll, setSendingAll] = useState(false);
  const [converting, setConverting] = useState(false);
  const [alert, setAlert] = useState<AlertProps | null>(null);

  const targetLabel =
    role === 'candidate'
      ? 'all early access candidates'
      : role === 'recruiter'
      ? 'all early access recruiters'
      : 'all early access users';

  const [search, setSearch] = useState('');
  const [platformRoleFilter, setPlatformRoleFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [page, setPage] = useState(0);

  const { values, setAllValues, handleValueChange } =
    useFormController<UserEarlyAccessResponse>(initialUserEarlyAccessResponse);

  const stats = useMemo(() => {
    const founding = data.filter((u) => u.foundingMember2026).length;
    const contributors = data.filter((u) => u.leadershipNetworkContributor).length;
    const companies = new Set(data.map((u) => (u.companyName || '').trim()).filter(Boolean)).size;
    return { total: data.length, founding, contributors, companies };
  }, [data]);

  const platformRoles = useMemo(() => {
    const set = new Set<string>();
    data.forEach((u) => {
      if (u.platformRole) set.add(u.platformRole);
    });
    return Array.from(set);
  }, [data]);

  const statuses = useMemo(() => {
    const set = new Set<string>();
    data.forEach((u) => {
      if (u.status) set.add(u.status);
    });
    return Array.from(set);
  }, [data]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return data.filter((u) => {
      const matchesSearch =
        !term ||
        `${u.firstName} ${u.lastName}`.toLowerCase().includes(term) ||
        (u.email || '').toLowerCase().includes(term) ||
        (u.companyName || '').toLowerCase().includes(term);
      const matchesRole = platformRoleFilter === 'ALL' || u.platformRole === platformRoleFilter;
      const matchesStatus = statusFilter === 'ALL' || normalize(u.status) === statusFilter;
      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [data, search, platformRoleFilter, statusFilter]);

  const totalElements = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalElements / PAGE_SIZE));
  const paged = useMemo(
    () => filtered.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE),
    [filtered, page]
  );

  useEffect(() => {
    setPage(0);
  }, [search, platformRoleFilter, statusFilter]);

  useEffect(() => {
    if (page > 0 && page >= totalPages) {
      setPage(totalPages - 1);
    }
  }, [page, totalPages]);

  const getValidUrl = (url: string) => {
    if (!url) return '#';
    return url.startsWith('http') ? url : `https://${url}`;
  };

  const showAlert = (variant: AlertProps['variant'], title: string, message: string) =>
    setAlert({ variant, title, message, autoCloseMs: 5000, onClose: () => setAlert(null) });

  const handleEditClick = (user: UserEarlyAccessResponse) => {
    setSelected(user);
    setAllValues({
      id: user.id || '',
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      companyName: user.companyName || '',
      email: user.email || '',
      role: user.role || '',
      platformRole: user.platformRole || '',
      annualLeadershipHiringVolume: user.annualLeadershipHiringVolume || '',
      linkedInUrl: user.linkedInUrl || '',
      foundingMember2026: user.foundingMember2026 ?? false,
      leadershipNetworkContributor: user.leadershipNetworkContributor ?? false,
      status: user.status || '',
      createdAt: user.createdAt || '',
      updatedAt: user.updatedAt || '',
    });
    setIsEditOpen(true);
  };

  const handleEditSubmit = async () => {
    if (!selected) return;
    setAlert(null);
    setSubmitting(true);
    try {
      const payload = {
        firstName: values.firstName,
        lastName: values.lastName,
        companyName: values.companyName,
        email: values.email,
        role: values.role,
        platformRole: values.platformRole,
        annualLeadershipHiringVolume: values.annualLeadershipHiringVolume,
        linkedInUrl: values.linkedInUrl,
        foundingMember2026: values.foundingMember2026,
        leadershipNetworkContributor: values.leadershipNetworkContributor,
        status: values.status,
      };

      const response = await updateEarlyAccess(selected.id, payload);

      if (response.success) {
        showAlert(
          'success',
          'Success...!',
          `${values.firstName} ${values.lastName} has been updated successfully.`
        );
        onChanged?.();
      } else {
        showAlert('error', 'Update Error', response.message);
      }
    } finally {
      setSubmitting(false);
      setIsEditOpen(false);
    }
  };

  const handleDeleteSubmit = async () => {
    if (!selected) return;
    setAlert(null);
    setSubmitting(true);
    try {
      const response = await deleteEarlyAccess(selected.id);
      const failed = (response as any)?.success === false;
      if (!failed) {
        showAlert(
          'success',
          'Success...!',
          `${selected.firstName} ${selected.lastName} has been deleted successfully.`
        );
        onChanged?.();
      } else {
        showAlert('error', 'Delete Error', response.message || 'Delete failed');
      }
    } finally {
      setSubmitting(false);
      setIsDeleteOpen(false);
    }
  };

  const handleSendAll = async () => {
    setAlert(null);
    setSendingAll(true);
    try {
      const response = await sendLaunchEmailToEarlyAccess(role);
      const list = (response as any)?.data;
      const sent = Array.isArray(list) ? list.length : 0;
      if (sent === 0) {
        showAlert(
          'warning',
          'Nothing Sent',
          `No email was sent. Everyone still pending had already registered on the portal and was marked as skipped.`
        );
      } else {
        showAlert(
          'success',
          'Success...!',
          `Launch email sent to ${sent} ${targetLabel}. They are now marked as sent and will not be emailed again.`
        );
      }
      setIsSendAllOpen(false);
      onChanged?.();
    } catch {
      showAlert('error', 'Send Error', 'Failed to send emails. Please try again.');
    } finally {
      setSendingAll(false);
    }
  };

  const handleConvertToProspect = async () => {
    if (!selected) return;
    setAlert(null);
    setConverting(true);
    try {
      const response = await convertEarlyAccessToProspect(selected.id);
      if (response.success) {
        const result = response.data;
        const name = `${selected.firstName} ${selected.lastName}`.trim();
        const parts = [`${name} has been added to Growth prospects with source WAITLIST.`];
        if (result?.invitation) {
          parts.push('Their invitation email has been sent.');
        } else {
          parts.push(
            result?.warning ||
              'The invitation email could not be sent — re-send it from Growth \u2192 Prospects.'
          );
        }
        showAlert('success', 'Added To Prospects', parts.join(' '));
        setIsConvertOpen(false);
        onChanged?.();
      } else {
        showAlert('error', 'Conversion Error', response.message || 'Failed to convert to prospect.');
      }
    } catch {
      showAlert('error', 'Conversion Error', 'Failed to convert to prospect. Please try again.');
    } finally {
      setConverting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
            <Rocket size={22} />
          </span>
          <div>
            <h1 className="text-xl font-black tracking-tight text-slate-100 md:text-2xl">
              {menuName} Directory
            </h1>
            <p className="text-xs font-semibold text-slate-400 md:text-sm">
              Pre-launch accounts and signups
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setAlert(null);
              setIsSendAllOpen(true);
            }}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-2.5 text-sm font-bold text-emerald-400 transition hover:bg-emerald-500/20"
          >
            <Send size={16} />
            Send All
          </button>
          <button
            onClick={() => onChanged?.()}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-4 py-2.5 text-sm font-bold text-slate-200 transition hover:border-cyan-500/40 hover:text-cyan-400"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {[
          { label: 'Total Signups', value: stats.total, icon: <Users size={18} />, accent: 'text-cyan-400 border-cyan-500/30 bg-cyan-500/10' },
          { label: 'Founding 2026', value: stats.founding, icon: <Sparkles size={18} />, accent: 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10' },
          { label: 'Leadership Contributors', value: stats.contributors, icon: <Network size={18} />, accent: 'text-amber-400 border-amber-500/30 bg-amber-500/10' },
          { label: 'Companies', value: stats.companies, icon: <Building2 size={18} />, accent: 'text-rose-400 border-rose-500/30 bg-rose-500/10' },
        ].map((card) => (
          <div key={card.label} className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
            <div className="flex items-center justify-between">
              <span className={`flex h-9 w-9 items-center justify-center rounded-xl border ${card.accent}`}>
                {card.icon}
              </span>
              <span className="text-2xl font-black text-slate-100">{card.value}</span>
            </div>
            <p className="mt-2 text-[11px] font-bold uppercase tracking-widest text-slate-500">
              {card.label}
            </p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="mb-6 rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email or company..."
              className={`${fieldClass} pl-9`}
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-slate-500" />
            <select
              value={platformRoleFilter}
              onChange={(e) => setPlatformRoleFilter(e.target.value)}
              className={fieldClass}
            >
              <option value="ALL">All Platform Roles</option>
              {platformRoles.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setStatusFilter('ALL')}
              className={`rounded-xl border px-3 py-2 text-xs font-bold transition ${
                statusFilter === 'ALL'
                  ? 'border-cyan-500/40 bg-cyan-500/10 text-cyan-400'
                  : 'border-slate-800 bg-slate-950/60 text-slate-400 hover:text-slate-200'
              }`}
            >
              All
            </button>
            {statuses.map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`rounded-xl border px-3 py-2 text-xs font-bold transition ${
                  statusFilter === s
                    ? 'border-cyan-500/40 bg-cyan-500/10 text-cyan-400'
                    : 'border-slate-800 bg-slate-950/60 text-slate-400 hover:text-slate-200'
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      </div>

      {alert && (
        <div className="mb-6">
          <Alert {...alert} onClose={() => setAlert(null)} />
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/40">
        <table className="w-full min-w-[1000px] text-left">
          <thead>
            <tr className="border-b border-slate-800 bg-slate-900/60">
              <th className="px-5 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">Full Name</th>
              <th className="px-5 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">Company</th>
              <th className="px-5 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">Email</th>
              <th className="px-5 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">Role</th>
              <th className="px-5 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">Platform Role</th>
              <th className="px-5 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">LinkedIn</th>
              <th className="px-5 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">Status</th>
              <th className="px-5 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">Joined</th>
              <th className="px-5 py-4 text-right text-[10px] font-black uppercase tracking-widest text-slate-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/70">
            {paged.map((user, idx) => (
              <motion.tr
                key={user.id || user.email || idx}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.03 }}
                className="transition hover:bg-slate-900/70"
              >
                <td className="px-5 py-4">
                  <div className="flex flex-col">
                    <span className="text-sm font-bold text-slate-100">
                      {user.firstName} {user.lastName}
                    </span>
                    {user.foundingMember2026 && (
                      <span className="mt-0.5 inline-flex w-fit items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-emerald-400">
                        <Sparkles size={10} /> Founding 2026
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-5 py-4">
                  <span className="text-sm font-semibold text-slate-300">
                    {user.companyName || '?'}
                  </span>
                </td>
                <td className="px-5 py-4">
                  {user.email ? (
                    <span title={user.email} className="text-sm font-semibold text-cyan-400">
                      {user.email.length > 22 ? `${user.email.slice(0, 22)}...` : user.email}
                    </span>
                  ) : (
                    <span className="text-slate-600">?</span>
                  )}
                </td>
                <td className="px-5 py-4">
                  <span className="text-sm font-semibold text-slate-300">{user.role || '?'}</span>
                </td>
                <td className="px-5 py-4">
                  <span className="text-sm font-semibold text-slate-300">
                    {user.platformRole || '?'}
                  </span>
                </td>
                <td className="px-5 py-4">
                  {user.linkedInUrl ? (
                    <a
                      href={getValidUrl(user.linkedInUrl)}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={user.linkedInUrl}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-cyan-400 hover:underline"
                    >
                      <Linkedin size={14} />
                      {user.linkedInUrl.length > 16
                        ? `${user.linkedInUrl.slice(0, 16)}...`
                        : user.linkedInUrl}
                    </a>
                  ) : (
                    <span className="text-slate-600">?</span>
                  )}
                </td>
                <td className="px-5 py-4">
                  <span
                    className={`inline-flex rounded-lg border px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${statusBadge(
                      user.status
                    )}`}
                  >
                    {user.status || 'UNKNOWN'}
                  </span>
                </td>
                <td className="px-5 py-4">
                  <span className="flex items-center gap-2 text-sm font-semibold text-slate-400">
                    <CalendarDays size={14} className="text-slate-600" />
                    {user.createdAt
                      ? new Date(user.createdAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })
                      : 'N/A'}
                  </span>
                </td>
                <td className="px-5 py-4">
                  <div className="flex justify-end gap-2">
                    {normalize(user.status) !== PROSPECT_SENT && (
                      <button
                        onClick={() => {
                          setSelected(user);
                          setIsConvertOpen(true);
                        }}
                        title="Add To Prospect"
                        className="rounded-xl border border-indigo-500/30 bg-indigo-500/10 p-2 text-indigo-400 transition hover:bg-indigo-500/20"
                      >
                        <UserPlus size={16} />
                      </button>
                    )}
                    <button
                      onClick={() => handleEditClick(user)}
                      title="Edit"
                      className="rounded-xl border border-cyan-500/30 bg-cyan-500/10 p-2 text-cyan-400 transition hover:bg-cyan-500/20"
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      onClick={() => {
                        setSelected(user);
                        setIsDeleteOpen(true);
                      }}
                      title="Delete"
                      className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-2 text-rose-400 transition hover:bg-rose-500/20"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </motion.tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={9} className="px-5 py-16 text-center text-sm font-semibold text-slate-500">
                  No early access users match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalElements > 0 && (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/40 px-4 py-2">
          <Pagination
            page={page}
            size={PAGE_SIZE}
            totalElements={totalElements}
            totalPages={totalPages}
            first={page === 0}
            last={page >= totalPages - 1}
            hasNext={page < totalPages - 1}
            hasPrevious={page > 0}
            setPage={setPage}
          />
        </div>
      )}

      {/* Edit modal */}
      <AnimatePresence>
        {isEditOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
            onClick={() => setIsEditOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 12 }}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-2xl"
            >
              <div className="mb-6 flex items-start justify-between">
                <div>
                  <h2 className="text-lg font-black text-slate-100">
                    Edit {selected?.firstName} {selected?.lastName}
                  </h2>
                  <p className="text-xs font-semibold text-slate-400">{menuName} account</p>
                </div>
                <button
                  onClick={() => setIsEditOpen(false)}
                  className="rounded-lg p-1.5 text-slate-500 transition hover:bg-slate-800 hover:text-slate-200"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelClass}>First Name</label>
                  <input
                    value={values.firstName}
                    onChange={(e) => handleValueChange('firstName')(e.target.value)}
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Last Name</label>
                  <input
                    value={values.lastName}
                    onChange={(e) => handleValueChange('lastName')(e.target.value)}
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Company</label>
                  <input
                    value={values.companyName}
                    onChange={(e) => handleValueChange('companyName')(e.target.value)}
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Email</label>
                  <input
                    value={values.email}
                    onChange={(e) => handleValueChange('email')(e.target.value)}
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Role</label>
                  <input
                    value={values.role}
                    onChange={(e) => handleValueChange('role')(e.target.value)}
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Platform Role</label>
                  <input
                    value={values.platformRole}
                    onChange={(e) => handleValueChange('platformRole')(e.target.value)}
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Annual Leadership Hiring Volume</label>
                  <input
                    value={values.annualLeadershipHiringVolume}
                    onChange={(e) =>
                      handleValueChange('annualLeadershipHiringVolume')(e.target.value)
                    }
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>LinkedIn URL</label>
                  <input
                    value={values.linkedInUrl}
                    onChange={(e) => handleValueChange('linkedInUrl')(e.target.value)}
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Founding Member 2026</label>
                  <select
                    value={values.foundingMember2026 ? 'true' : 'false'}
                    onChange={(e) => handleValueChange('foundingMember2026')(e.target.value === 'true')}
                    className={fieldClass}
                  >
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Leadership Network Contributor</label>
                  <select
                    value={values.leadershipNetworkContributor ? 'true' : 'false'}
                    onChange={(e) =>
                      handleValueChange('leadershipNetworkContributor')(e.target.value === 'true')
                    }
                    className={fieldClass}
                  >
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className={labelClass}>Status</label>
                  <select
                    value={values.status}
                    onChange={(e) => handleValueChange('status')(e.target.value)}
                    className={fieldClass}
                  >
                    <option value="">Select status</option>
                    {Array.from(new Set([values.status, ...STATUS_OPTIONS].filter(Boolean))).map(
                      (s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      )
                    )}
                  </select>
                </div>
              </div>

              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => setIsEditOpen(false)}
                  className="flex-1 rounded-xl border border-slate-800 bg-slate-950/60 py-3 text-sm font-bold text-slate-300 transition hover:text-slate-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditSubmit}
                  disabled={submitting}
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-cyan-500 py-3 text-sm font-black text-slate-950 transition hover:bg-cyan-400 disabled:opacity-60"
                >
                  {submitting ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                  Update
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete modal */}
      <AnimatePresence>
        {isDeleteOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
            onClick={() => setIsDeleteOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 12 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 p-6 text-center shadow-2xl"
            >
              <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl border border-rose-500/30 bg-rose-500/10 text-rose-400">
                <AlertTriangle size={28} />
              </div>
              <h2 className="text-lg font-black text-slate-100">Confirm Removal</h2>
              <p className="mt-2 px-2 text-sm font-medium text-slate-400">
                Are you sure you want to delete{' '}
                <span className="font-bold text-slate-100">
                  {selected?.firstName} {selected?.lastName}
                </span>
                ?
              </p>
              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => setIsDeleteOpen(false)}
                  className="flex-1 rounded-xl border border-slate-800 bg-slate-950/60 py-3 text-sm font-bold text-slate-300 transition hover:text-slate-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteSubmit}
                  disabled={submitting}
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-rose-500 py-3 text-sm font-black text-white transition hover:bg-rose-400 disabled:opacity-60"
                >
                  {submitting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Convert to Prospect modal */}
      <AnimatePresence>
        {isConvertOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
            onClick={() => setIsConvertOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 12 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-2xl"
            >
              <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl border border-indigo-500/30 bg-indigo-500/10 text-indigo-400">
                <UserPlus size={28} />
              </div>
              <h2 className="text-center text-lg font-black text-slate-100">Add To Prospect</h2>
              <p className="mt-2 px-2 text-center text-sm font-medium text-slate-400">
                Convert{' '}
                <span className="font-bold text-slate-100">
                  {selected?.firstName} {selected?.lastName}
                </span>{' '}
                into a Growth prospect (source <span className="font-bold text-slate-200">WAITLIST</span>),
                send their invitation email, and mark this row as {PROSPECT_SENT}?
              </p>
              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => setIsConvertOpen(false)}
                  className="flex-1 rounded-xl border border-slate-800 bg-slate-950/60 py-3 text-sm font-bold text-slate-300 transition hover:text-slate-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConvertToProspect}
                  disabled={converting}
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-indigo-500 py-3 text-sm font-black text-white transition hover:bg-indigo-400 disabled:opacity-60"
                >
                  {converting ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <UserPlus size={16} />
                  )}
                  Add To Prospect
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Send All modal */}
      <AnimatePresence>
        {isSendAllOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
            onClick={() => setIsSendAllOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 12 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 p-6 text-center shadow-2xl"
            >
              <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl border border-emerald-500/30 bg-emerald-500/10 text-emerald-400">
                <Send size={28} />
              </div>
              <h2 className="text-lg font-black text-slate-100">Send Launch Email</h2>
              <p className="mt-2 px-2 text-sm font-medium text-slate-400">
                Send the &quot;NexGent is Officially Live&quot; email to{' '}
                <span className="font-bold text-emerald-400">{targetLabel}</span> in one shot?
              </p>
              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => setIsSendAllOpen(false)}
                  className="flex-1 rounded-xl border border-slate-800 bg-slate-950/60 py-3 text-sm font-bold text-slate-300 transition hover:text-slate-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendAll}
                  disabled={sendingAll}
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-emerald-500 py-3 text-sm font-black text-slate-950 transition hover:bg-emerald-400 disabled:opacity-60"
                >
                  {sendingAll ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  Send All
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EarlyAccessUsersComponent;
